import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { PaginationPage, PaginationResponse, Setting } from 'src/app/classes';
import { serverLocation } from 'src/app/globals';
import { WebsocketService } from 'src/app/services/websocket.service';
import { SessionService } from '../../shared/services/session.service';
import { TokenService } from '../../shared/services/token.service';
import { NewsfeedNotification } from '../classes';
import { NewsfeedNotificationObservables } from '../globals';

@Injectable({
  providedIn: 'root'
})
export class NewsfeedService {

  private _newsfeedVisible = new BehaviorSubject(false);
  newsfeedVisible$ = this._newsfeedVisible.asObservable();

  changeNotifications: NewsfeedNotification[] = [];
  private _changeNotificationReceived = new Subject<NewsfeedNotification>();
  changeNotificationReceived$ = this._changeNotificationReceived.asObservable();

  private _totalChangeNotificationCount = new BehaviorSubject(0);
  totalChangeNotificationCount$ = this._totalChangeNotificationCount.asObservable();

  private _subscriptionChange = new Subject();
  subscriptionChange$ = this._subscriptionChange.asObservable();

  private _auditStatus: { [key: string]: ReplaySubject<any> } = {};

  page: PaginationPage = { 'pageNumber': 1, 'pageSize': 10 };

  constructor(
    private _sessionService: SessionService,
    private _httpClient: HttpClient,
    private _tokenService: TokenService,
    private _snackBar: MatSnackBar,
    private _websocketService: WebsocketService
  ) {
    this.setUp();
  }

  getAuditStatus(observable: number, guid: string): Observable<any> {
    if (!this._auditStatus[guid]) {
      this._auditStatus[guid] = new ReplaySubject(1);
      var url = null;
      switch (observable) {
        case NewsfeedNotificationObservables.IssueChange:
        case NewsfeedNotificationObservables.IssueCreation:
        case NewsfeedNotificationObservables.IssueDeletion:
          url = `${serverLocation}/api/issue/audit-status/${guid}`;
          break;

        case NewsfeedNotificationObservables.IssueItemChange:
        case NewsfeedNotificationObservables.IssueItemCreation:
        case NewsfeedNotificationObservables.IssueItemDeletion:
          url = `${serverLocation}/api/issue-item/audit-status/${guid}`;
          break;
      }
      if (typeof url !== 'string') return throwError(`cannot resolve newsfeed observable: ${observable}`);
      this._httpClient.get(url, {
        headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
      }).subscribe(this._auditStatus[guid]);
    }
    return this._auditStatus[guid];
  }

  subscriptionChanged = () => this._subscriptionChange.next();

  loadInitialChanges(): Observable<Object> {
    let subject = new Subject();
    this.page.pageNumber = 1;
    this._loadChanges().subscribe(
      (result: PaginationResponse<NewsfeedNotification[]>) => {
        this.changeNotifications = result.content;
        this._totalChangeNotificationCount.next(result.totalItems);
        subject.next();
      },
      (error) => subject.error(error),
      () => subject.complete()
    );
    return subject.asObservable();
  }

  loadMoreChanges(): Observable<Object> {
    let subject = new Subject();
    this.totalChangeNotificationCount$.pipe(
      take(1),
      switchMap((result: number) => {
        if (result <= this.changeNotifications.length) {
          this._snackBar.open("Es können keine weiteren Einträge abgerufen werden", "Ok", {
            duration: 3000
          });
          return of(null);
        } else {
          this.page.pageNumber++;
          return this._loadChanges();
        }
      })
    ).subscribe(
      (result: PaginationResponse<NewsfeedNotification[]>) => {
        if (result) {
          this.changeNotifications = [...this.changeNotifications, ...result.content];
          this._totalChangeNotificationCount.next(result.totalItems);
          subject.next();
        }
      },
      (error) => subject.error(error),
      () => subject.complete()
    );
    return subject.asObservable();
  }

  loadSubscriptions(): Observable<number[]> {
    return this._httpClient.get(`${serverLocation}/api/newsfeed/subscriptions`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<number[]>;
  }

  receivedChangeNotification(newsfeedNotification: any) {
    this._changeNotificationReceived.next({
      fired: newsfeedNotification.Fired,
      guid: newsfeedNotification.Guid,
      identifier: newsfeedNotification.Identifier,
      note: newsfeedNotification.Note,
      observable: newsfeedNotification.Observable,
      performer: newsfeedNotification.Performer
    } as NewsfeedNotification);
    this._totalChangeNotificationCount.next(this._totalChangeNotificationCount.value + 1);
  }

  setNewsfeedVisibility = (value: boolean) => this._newsfeedVisible.next(value);

  setUp() {
    this._sessionService.getSettingByName(SessionService.globalSettingNames.SHOW_NEWSFEED).subscribe((value: Setting) => this._newsfeedVisible.next(value && value.stgvalue === 'true'));
    this._changeNotificationReceived.subscribe((notifcation: NewsfeedNotification) => this.changeNotifications.unshift(notifcation));
    this._websocketService.newsfeedNotificationReceived$.subscribe({
      next: (notification) => this.receivedChangeNotification(notification)
    });
  }

  subscribeObservable(identifier: number): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/newsfeed/subscribe/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  unsubscribeObservable(identifier: number): Observable<Object> {
    return this._httpClient.get(`${serverLocation}/api/newsfeed/unsubscribe/${identifier}`, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<Object>;
  }

  private _loadChanges(): Observable<PaginationResponse<NewsfeedNotification[]>> {
    return this._httpClient.post(`${serverLocation}/api/newsfeed/content`, this.page, {
      headers: new HttpHeaders().set('Authorization', `Bearer ${this._tokenService.token}`)
    }) as Observable<PaginationResponse<NewsfeedNotification[]>>;
  }
}
